import React, { useEffect, useState } from 'react';

import { checkBorrower } from 'prequalificationV2/utils/checkBorrower';
import { PreQualificationStep, setAccount, setPreQualificationStep } from 'prequalificationV2/actions';
import CodeVerificationForm from 'components/CodeVerificationForm';
import Loader from 'components/Loader';
import { LoanInformationVariables } from 'prequalificationV2/enums/LoanInformationVariables';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import useAppNavigate from 'hooks/useAppNavigate';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import { FailureNotificationType } from 'notifications';
import * as notificationManager from 'notifications';
import { applicationApi, ApplicationDto } from 'prequalificationV2/api/ApplicationApi';
import { preQualificationApi } from 'prequalificationV2/api/PreQualification/PreQualificationApi';
import { getPostLoginNavigation } from 'prequalificationV2/pages/PreQualification/prequalification.utils';
import { NextStepDestination } from 'prequalificationV2/utils/nextStepNavigation';
import { ApplicationType } from 'enums/ApplicationType';
import FormHeader from 'prequalificationV2/components/FormHeader';
import { authApi } from 'api/auth/AuthApi';
import { getBorrowerByToken, otpVerify, updateBorrower } from 'prequalificationV2/thunks';
import { Button } from 'components/Buttons/Button';
import { ButtonType } from 'enums/ButtonType';
import { AppRoutes } from 'prequalificationV2/routes/routes.enums';
import { setPrequalificationPortal } from 'prequalificationV2/actions/prequalificationPortal';
import { BorrowerDto } from 'prequalificationV2/api/BorrowersApi';
import { updateApplicationInStore } from 'prequalificationV2/utils/updateApplicationInStore';

import styles from './OtpLogin.module.scss';

interface OtpLoginProps {
  isResumeProcessLink?: boolean;
}

const OtpLogin = ({ isResumeProcessLink = false }: OtpLoginProps) => {
  const dispatch = useAppDispatch();
  const navigator = useAppNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCodeVerifying, setIsCodeVerifying] = useState<boolean>(false);
  const [isCodeValid, setIsCodeValid] = useState<boolean | null>(null);

  const {
    account: { id, email: accountEmail },
    application: { [LoanInformationVariables.LoanType]: loanType },
    borrower: {
      firstName,
      lastName,
      email,
      phone,
      personalIdNumber,
      fullConsentAgreed: hasFullConsentAgreed,
      city,
      state: borrowerState,
      dateOfBirth,
      streetName,
      streetNumber,
      zip,
      county,
    },
  } = useAppSelector((state) => state);

  const handleSendCode = async () => {
    try {
      setIsCodeValid(null);

      await authApi.sendOtpCode(id);
    } catch (error: any) {
      if (error.statusCode === 429) {
        notificationManager.notifyFailure(FailureNotificationType.TooManyRequest);
      } else {
        notificationManager.notifyFailure(FailureNotificationType.UnknownError);
      }
    }
  };

  const handleResendCode = async () => {
    try {
      setIsCodeValid(null);

      await authApi.resendOtpCode(id);
    } catch (error: any) {
      if (error.statusCode === 429) {
        notificationManager.notifyFailure(FailureNotificationType.TooManyRequest);
      } else {
        notificationManager.notifyFailure(FailureNotificationType.UnknownError);
      }
    }
  };

  useEffect(() => {
    handleSendCode();
  }, [id]);

  const prevStep = personalIdNumber ? PreQualificationStep.BorrowerInfo : PreQualificationStep.BorrowerIdentity;

  const redirect = async (
    isLocked: boolean,
    activeApplication: { borrowerApplication?: ApplicationDto; hasCobborowerApplication: boolean },
  ) => {
    try {
      setIsLoading(true);

      const { borrowerApplication, hasCobborowerApplication } = activeApplication;

      if (isLocked && !borrowerApplication && !hasCobborowerApplication) {
        dispatch(setPreQualificationStep(PreQualificationStep.NotAbleToAccept));

        return;
      }

      let navigation;

      if (borrowerApplication) {
        navigation = await getPostLoginNavigation(loanType);
        updateApplicationInStore(borrowerApplication, dispatch);
      } else {
        let step = PreQualificationStep.BorrowerInfo;

        if (loanType === ApplicationType.Individual) {
          step = PreQualificationStep.LoanInformation;
        } else if (loanType === ApplicationType.Joint) {
          step = PreQualificationStep.CoBorrowerInfo;
        }

        // Brand-new borrower without an application yet — start the prequal flow.
        navigation = {
          destination: NextStepDestination.Prequalification,
          prequalificationStep: step,
        };
      }

      checkBorrower({
        borrowerApplication: borrowerApplication,
        hasCobborowerApplication: hasCobborowerApplication,
        dispatch: dispatch,
        navigator: navigator,
        navigation: navigation,
        shouldShowAlreadyHaveActiveApplication: !isResumeProcessLink,
      });
    } catch (error) {
      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (code: string) => {
    if (!id) {
      return;
    }

    try {
      setIsCodeVerifying(true);

      const response = await dispatch(otpVerify({ code: code, borrowerId: id })).unwrap();

      dispatch(setPrequalificationPortal({ isBorrowerAuthorized: true }));

      if (!isResumeProcessLink) {
        try {
          await preQualificationApi.updateBorrowerMarketingConsent({
            email: email,
            fullConsentAgreed: hasFullConsentAgreed,
            consentAgreed: true,
            personalIdNumber: personalIdNumber || null,
          });
        } catch (error) {
          notificationManager.notifyFailure(FailureNotificationType.UnknownError);
        }
      }

      const getActiveApplicationDto = await applicationApi.getActiveApplication();

      const hasActiveApplication =
        !!getActiveApplicationDto.borrowerApplication || getActiveApplicationDto.hasCobborowerApplication;

      let borrower: BorrowerDto;

      if (!isResumeProcessLink && personalIdNumber && !hasActiveApplication) {
        //Login after Borrower info step, no active application found — safe to sync info to LOS
        borrower = await dispatch(
          updateBorrower({
            firstName: firstName,
            lastName: lastName,
            address: {
              streetName: streetName,
              streetNumber: streetNumber,
              city: city,
              stateOrProvince: borrowerState,
              zipOrPostalCode: zip,
              county: county,
            },
            dateOfBirth: dateOfBirth,
            phone: phone,
            email: email,
          }),
        ).unwrap();
      } else {
        //Login after Borrower identity step
        borrower = await dispatch(getBorrowerByToken()).unwrap();
      }

      dispatch(
        setAccount({
          email: borrower.variables.borrower_email,
          phone: borrower.variables.borrower_phone,
          id: borrower.id,
        }),
      );

      setIsCodeValid(true);

      await redirect(response.locked, getActiveApplicationDto);
    } catch (error) {
      setIsCodeValid(false);
    } finally {
      setIsCodeVerifying(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <div className={styles.container}>
          {isResumeProcessLink ? (
            <FormHeader title="Email Verification" />
          ) : (
            <FormHeader title="Email Verification" percent={15} step={1} totalSteps={4} />
          )}
          <CodeVerificationForm
            alternativeFormHeader={
              <p className={styles.formHeaderText}>
                {isResumeProcessLink
                  ? `Please enter a 6-digit code we’ve sent to ${accountEmail} to verify your identity and continue with your application.`
                  : `It looks like there's already a record associated with the information you provided. Please enter a 6-digit code we’ve sent to your Email to verify your identity.`}
              </p>
            }
            handleVerificationCodeComplete={handleVerifyCode}
            isCodeVerifying={isCodeVerifying}
            isCodeValid={isCodeValid}
            onResendCode={handleResendCode}
            formBodyClassName={styles.codeVerificationForm}
          >
            <div className={styles.additionalInfo}>
              <p className={styles.additionalInfoText}>For any assistance, please contact your HFS Representative.</p>
              <p className={styles.additionalContactText}>(800) 254-9560</p>
              <p className={styles.additionalContactText}>info@hfsfin.com</p>
              <p className={styles.additionalContactText}>www.hfsfin.com</p>
            </div>
          </CodeVerificationForm>
          {!isResumeProcessLink && (
            <div className={styles.backButtonContainer}>
              <Button
                className={styles.backButton}
                buttonType={ButtonType.Secondary}
                withArrow
                link={AppRoutes.Prequalification}
                applicationStep={prevStep}
              >
                Back
              </Button>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default OtpLogin;
